import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { SET_AUDIO_MUTED, SET_SCREENSHARE_MUTED, SET_VIDEO_MUTED } from '../base/media/actionTypes';
import { setAudioMuted, setScreenshareMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE, SCREENSHARE_MUTISM_AUTHORITY, VIDEO_MUTISM_AUTHORITY, VIDEO_TYPE } from '../base/media/constants';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT, PARTICIPANT_UPDATED } from '../base/participants/actionTypes';
import { participantUpdated } from '../base/participants/actions';
import { LOCAL_PARTICIPANT_DEFAULT_ID } from '../base/participants/constants';
import { getLocalParticipant, getParticipantById, getParticipantDisplayName, getRemoteParticipants, isScreenShareParticipant } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { replaceLocalTrack, toggleScreensharing } from '../base/tracks/actions';
import { getLocalDesktopTrack } from '../base/tracks/functions.any';
import { updateRemoteParticipants } from '../filmstrip/functions';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { isScreenVideoShared } from '../screen-share/functions';
import { ACCOUNT_IDENTITY_PROPERTY, MEDIA_UPLINK_OWNER_PROPERTY } from './constants';
import { agentDebugLog } from './agentDebugLog';
import { getAccountIdentityId, getWinningUplinkClaim, encodeUplinkClaim, hasSiblingDevices, isLocalMediaUplinkOwner, isMultiDeviceModeEnabled, parseUplinkClaim } from './functions';
import logger from './logger';
/** 上次对本端执行 force 的时间，用于抑制声明风暴下的重复 force */
let lastForceReceiveOnlyAtMs = 0;
/** force 最小间隔（毫秒），避免同一次抢占被刷成数十次 mute */
const FORCE_RECEIVE_ONLY_MIN_INTERVAL_MS = 500;
/**
 * 汇总本端与远端的账号身份 / 上行声明，供第三人视角排查.
 *
 * @param {IStore} store - store.
 * @param {string} reason - 触发原因.
 * @returns {void}
 */
function logParticipantInventory(store, reason) {
    const state = store.getState();
    const local = getLocalParticipant(state);
    const remotes = [];
    getRemoteParticipants(state).forEach(p => {
        remotes.push({
            id: p.id,
            name: getParticipantDisplayName(state, p.id),
            accountId: getAccountIdentityId(p) || null,
            claim: p.mediaUplinkOwner || null,
            fake: Boolean(p.fakeParticipant)
        });
    });
    agentDebugLog('H2', 'multi-device/middleware.ts:inventory', 'participant inventory', {
        reason,
        localId: local?.id || null,
        localName: local?.id ? getParticipantDisplayName(state, local.id) : null,
        localAccountId: getAccountIdentityId(local) || null,
        localClaim: local?.mediaUplinkOwner || null,
        isOwner: isLocalMediaUplinkOwner(state),
        remoteCount: remotes.length,
        remotes
    });
}
/**
 * 从 JWT / 本端参会者解析账号 id，并写入 Presence，供对端识别同账号。
 *
 * @param {IStore} store - store.
 * @returns {string|undefined} 已发布的账号 id.
 */
function publishAccountIdentity({ dispatch, getState }) {
    const state = getState();
    const conference = getCurrentConference(state);
    const local = getLocalParticipant(state);
    const jwtUserId = state['features/base/jwt']?.user?.id;
    const accountId = (jwtUserId && String(jwtUserId))
        || getAccountIdentityId(local);
    if (!conference || !local?.id || !accountId) {
        return undefined;
    }
    logger.info(`[multi-device] 发布账号身份 | endpointId=${local.id} accountId=${accountId}`);
    conference.setLocalParticipantProperty(ACCOUNT_IDENTITY_PROPERTY, accountId);
    dispatch(participantUpdated({
        id: local.id,
        local: true,
        [ACCOUNT_IDENTITY_PROPERTY]: accountId
    }));
    return accountId;
}
/**
 * 声明本端为上行主设备，并写入 Presence。
 *
 * @param {IStore} store - store.
 * @returns {boolean} 是否已成功写入 Presence 声明.
 */
function claimMediaUplinkOwner({ dispatch, getState }) {
    const state = getState();
    const conference = getCurrentConference(state);
    const local = getLocalParticipant(state);
    if (!conference || !local?.id || local.id === LOCAL_PARTICIPANT_DEFAULT_ID) {
        agentDebugLog('H9', 'multi-device/middleware.ts:claimMediaUplinkOwner', 'claim aborted not ready', {
            localId: local?.id || null,
            hasConference: Boolean(conference)
        });
        return false;
    }
    // 抢占前先发布账号身份，并写入带 accountId 的声明，避免兄弟端身份未同步时匹配失败
    const accountId = publishAccountIdentity({ dispatch, getState })
        || getAccountIdentityId(getLocalParticipant(getState()));
    if (!accountId) {
        logger.warn('[multi-device] 无法夺取上行：缺少账号身份');
        agentDebugLog('H2', 'multi-device/middleware.ts:claimMediaUplinkOwner', 'claim aborted no accountId', {
            localId: local.id
        });
        return false;
    }
    const claimValue = encodeUplinkClaim(accountId, local.id, Date.now());
    logger.info(`[multi-device] 夺取上行主设备 | endpointId=${local.id} claim=${claimValue}`);
    conference.setLocalParticipantProperty(MEDIA_UPLINK_OWNER_PROPERTY, claimValue);
    dispatch(participantUpdated({
        id: local.id,
        local: true,
        [MEDIA_UPLINK_OWNER_PROPERTY]: claimValue,
        [ACCOUNT_IDENTITY_PROPERTY]: accountId
    }));
    agentDebugLog('H1', 'multi-device/middleware.ts:claimMediaUplinkOwner', 'claimed uplink with accountId', {
        localId: local.id,
        accountId,
        claimValue,
        hasSiblings: hasSiblingDevices(getState())
    });
    return true;
}
/**
 * 本端是否已经处于只收不发（避免重复 force 刷 mute）。
 *
 * @param {ReturnType<IStore['getState']>} state - state.
 * @returns {boolean}
 */
function isAlreadyReceiveOnly(state) {
    const audioMuted = Boolean(state['features/base/media']?.audio?.muted);
    const videoMuted = Boolean(state['features/base/media']?.video?.muted);
    const screenshareMuted = Boolean(state['features/base/media']?.screenshare?.muted);
    const desktop = getLocalDesktopTrack(state['features/base/tracks']);
    const hasLiveDesktop = Boolean(desktop?.jitsiTrack && !desktop.jitsiTrack.isMuted());
    return audioMuted && videoMuted && screenshareMuted && !hasLiveDesktop;
}
/**
 * 本端失去上行权时强制停发（含屏幕共享）。
 *
 * @param {IStore} store - store.
 * @returns {void}
 */
function forceLocalReceiveOnly({ dispatch, getState }) {
    const state = getState();
    const conference = getCurrentConference(state);
    const local = getLocalParticipant(state);
    const accountId = getAccountIdentityId(local);
    const winning = accountId ? getWinningUplinkClaim(state, accountId) : undefined;
    const isOwner = isLocalMediaUplinkOwner(state);
    if (isOwner) {
        agentDebugLog('H3', 'multi-device/middleware.ts:forceLocalReceiveOnly', 'skip force still owner', {
            localId: local?.id || null,
            accountId: accountId || null,
            winning: winning || null
        });
        return;
    }
    const now = Date.now();
    if (isAlreadyReceiveOnly(state)
        && now - lastForceReceiveOnlyAtMs < FORCE_RECEIVE_ONLY_MIN_INTERVAL_MS) {
        agentDebugLog('H8', 'multi-device/middleware.ts:forceLocalReceiveOnly', 'skip force already receive-only', {
            localId: local?.id || null,
            winning: winning || null
        });
        return;
    }
    lastForceReceiveOnlyAtMs = now;
    const desktopBefore = getLocalDesktopTrack(state['features/base/tracks']);
    const screenSharedBefore = isScreenVideoShared(state);
    logger.info('[multi-device] 本端非上行主设备，强制静音停画停共享');
    agentDebugLog('H3', 'multi-device/middleware.ts:forceLocalReceiveOnly', 'force receive-only stop share', {
        localId: local?.id || null,
        accountId: accountId || null,
        winning: winning || null,
        screenshareMuted: state['features/base/media']?.screenshare?.muted,
        screenSharedBefore,
        hasDesktopTrack: Boolean(desktopBefore?.jitsiTrack)
    });
    // 清掉本端上行声明
    if (conference && local?.id) {
        try {
            conference.setLocalParticipantProperty(MEDIA_UPLINK_OWNER_PROPERTY, '');
            dispatch(participantUpdated({
                id: local.id,
                local: true,
                [MEDIA_UPLINK_OWNER_PROPERTY]: ''
            }));
        }
        catch (e) {
            logger.warn('[multi-device] 清理本端上行声明失败', e);
        }
    }
    dispatch(setAudioMuted(true));
    dispatch(setVideoMuted(true, VIDEO_MUTISM_AUTHORITY.USER));
    dispatch(setScreenshareMuted(true, SCREENSHARE_MUTISM_AUTHORITY.USER));
    Promise.resolve(dispatch(toggleScreensharing(false)))
        .catch((e) => {
        logger.warn('[multi-device] toggleScreensharing(false) 失败', e);
    })
        .then(() => {
        const afterState = getState();
        const desktopAfter = getLocalDesktopTrack(afterState['features/base/tracks']);
        if (desktopAfter?.jitsiTrack) {
            logger.warn('[multi-device] toggle 后仍有桌面轨，dispose 兜底');
            agentDebugLog('H3', 'multi-device/middleware.ts:forceLocalReceiveOnly', 'dispose desktop track after toggle', {
                localId: local?.id || null,
                screenSharedAfter: isScreenVideoShared(afterState),
                trackMuted: Boolean(desktopAfter.jitsiTrack.isMuted())
            });
            dispatch(replaceLocalTrack(desktopAfter.jitsiTrack, null));
        }
    });
}
/**
 * 后开抢占：本端点开麦/摄像头/共享时发布 claim，发布成功即视为取得上行权。
 * 入会前（id 仍为 local / 无 conference）不拦截 unmute，避免「共享后无法开摄像头」误伤。
 *
 * @param {IStore} store - store.
 * @param {string} reason - 触发来源.
 * @param {boolean} notifySiblings - 是否提示.
 * @returns {boolean} 是否允许本次 unmute.
 */
function claimUplinkForLocalMedia(store, reason, notifySiblings = false) {
    agentDebugLog('H1', 'multi-device/middleware.ts:claimUplinkForLocalMedia', 'claim triggered', {
        reason
    });
    const state = store.getState();
    const conference = getCurrentConference(state);
    const local = getLocalParticipant(state);
    // 入会前：放行，不抢占（否则会因 localId=local / 无 conference 误拒 unmute）
    if (!conference || !local?.id || local.id === LOCAL_PARTICIPANT_DEFAULT_ID) {
        agentDebugLog('H9', 'multi-device/middleware.ts:claimUplinkForLocalMedia', 'prejoin allow unmute', {
            reason,
            localId: local?.id || null,
            hasConference: Boolean(conference)
        });
        return true;
    }
    const alreadyOwner = isLocalMediaUplinkOwner(state);
    const isTrackPath = reason.indexOf('TRACK_') === 0;
    // TRACK 补路径：已是主设备则不刷 ts，防止声明风暴
    if (alreadyOwner && isTrackPath) {
        agentDebugLog('H7', 'multi-device/middleware.ts:claimUplinkForLocalMedia', 'skip claim already owner', {
            reason
        });
        return true;
    }
    // 同设备自抢占：开摄像头时若仍在共享，先停共享（否则桌面轨占住视频位，摄像头无法真正打开）
    if (reason === SET_VIDEO_MUTED && isScreenVideoShared(state)) {
        agentDebugLog('H17', 'multi-device/middleware.ts:claimUplinkForLocalMedia', 'self-preempt stop share for camera', {
            reason,
            localId: local.id
        });
        store.dispatch(toggleScreensharing(false));
    }
    const published = claimMediaUplinkOwner(store);
    agentDebugLog('H6', 'multi-device/middleware.ts:claimUplinkForLocalMedia', published ? 'claim published allow unmute' : 'claim publish failed', {
        reason,
        published,
        localId: getLocalParticipant(store.getState())?.id || null,
        accountId: getAccountIdentityId(getLocalParticipant(store.getState())) || null,
        claimValue: getLocalParticipant(store.getState())?.mediaUplinkOwner || null,
        winning: (() => {
            const accountId = getAccountIdentityId(getLocalParticipant(store.getState()));
            return accountId ? getWinningUplinkClaim(store.getState(), accountId) : null;
        })()
    });
    if (!published) {
        // 仅「缺账号身份」才拒绝；conference/id 已在上方放行
        return false;
    }
    if (notifySiblings && hasSiblingDevices(store.getState())) {
        store.dispatch(showNotification({
            titleKey: 'notify.multiDeviceUplinkTaken',
            descriptionKey: 'notify.multiDeviceUplinkTakenDescription',
            uid: 'multi-device-uplink'
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    }
    return true;
}
/**
 * 同账号多设备：上行互斥中间件。
 */
MiddlewareRegistry.register((store) => next => action => {
    const { dispatch, getState } = store;
    switch (action.type) {
        case SET_AUDIO_MUTED:
        case SET_VIDEO_MUTED:
        case SET_SCREENSHARE_MUTED: {
            if (!isMultiDeviceModeEnabled(getState())) {
                break;
            }
            agentDebugLog('H1', 'multi-device/middleware.ts:SET_*_MUTED', 'mute action seen', {
                type: action.type,
                muted: action.muted,
                willClaim: !action.muted
            });
            // 尝试开麦/开摄像头/开共享 → 后开抢占
            // 注意：屏幕共享 muted 是 bitmask 数值，必须用 !muted（0===false 为假）
            if (!action.muted) {
                const owned = claimUplinkForLocalMedia(store, action.type, true);
                if (!owned) {
                    // 抢占失败：拦截本次 unmute，避免「先打开再被 force 关掉」
                    agentDebugLog('H6', 'multi-device/middleware.ts:SET_*_MUTED', 'reject unmute claim lost', {
                        type: action.type
                    });
                    return;
                }
            }
            break;
        }
        case TRACK_ADDED: {
            if (!isMultiDeviceModeEnabled(getState())) {
                break;
            }
            const track = action.track;
            // 本地未静音的摄像头 / 桌面 / 音频轨加入时补抢占（覆盖部分未走 SET_*_MUTED 的路径）
            if (track?.local && track.jitsiTrack && !track.jitsiTrack.isMuted()) {
                const isDesktop = track.videoType === VIDEO_TYPE.DESKTOP
                    || track.mediaType === MEDIA_TYPE.SCREENSHARE;
                const isCamera = track.mediaType === MEDIA_TYPE.VIDEO && !isDesktop;
                const isAudio = track.mediaType === MEDIA_TYPE.AUDIO;
                if (isDesktop || isCamera || isAudio) {
                    agentDebugLog('H1', 'multi-device/middleware.ts:TRACK_ADDED', 'local uplink track added', {
                        mediaType: track.mediaType || null,
                        videoType: track.videoType || null
                    });
                    const result = next(action);
                    const owned = claimUplinkForLocalMedia(store, `TRACK_ADDED:${track.mediaType || track.videoType}`);
                    // 轨已加入但抢占失败：收回上行（停共享/静音）
                    if (!owned) {
                        forceLocalReceiveOnly(store);
                    }
                    return result;
                }
            }
            break;
        }
        case TRACK_UPDATED: {
            if (!isMultiDeviceModeEnabled(getState())) {
                break;
            }
            const track = action.track;
            // 仅在「本地轨从静音变为未静音」时抢占，避免 TRACK_UPDATED 高频刷 Presence
            if (track?.local
                && typeof track.muted === 'boolean'
                && track.muted === false
                && track.jitsiTrack) {
                const isDesktop = track.videoType === VIDEO_TYPE.DESKTOP
                    || track.mediaType === MEDIA_TYPE.SCREENSHARE
                    || track.jitsiTrack.getVideoType?.() === VIDEO_TYPE.DESKTOP;
                const mediaType = track.mediaType || track.jitsiTrack.getType?.();
                const isCamera = mediaType === MEDIA_TYPE.VIDEO && !isDesktop;
                const isAudio = mediaType === MEDIA_TYPE.AUDIO;
                if (isDesktop || isCamera || isAudio) {
                    agentDebugLog('H4', 'multi-device/middleware.ts:TRACK_UPDATED', 'local track unmuted', {
                        mediaType: mediaType || null,
                        isDesktop
                    });
                    const owned = claimUplinkForLocalMedia(store, `TRACK_UPDATED_UNMUTE:${mediaType || 'unknown'}`);
                    if (!owned) {
                        agentDebugLog('H6', 'multi-device/middleware.ts:TRACK_UPDATED', 'reject track unmute claim lost', {
                            mediaType: mediaType || null
                        });
                        forceLocalReceiveOnly(store);
                        return;
                    }
                    return next(action);
                }
            }
            break;
        }
        case PARTICIPANT_UPDATED:
        case PARTICIPANT_JOINED: {
            if (!isMultiDeviceModeEnabled(getState())) {
                break;
            }
            const result = next(action);
            const stateAfter = getState();
            const local = getLocalParticipant(stateAfter);
            const updatedId = action.participant?.id;
            const isRemoteUpdate = Boolean(updatedId && local?.id && updatedId !== local.id);
            const remote = updatedId ? getParticipantById(stateAfter, updatedId) : undefined;
            // 虚拟屏幕共享参与者无账号身份，绝不能触发同账号 force
            if (isRemoteUpdate && (isScreenShareParticipant(remote) || remote?.fakeParticipant
                || action.participant?.fakeParticipant)) {
                return result;
            }
            const claimRaw = (typeof action.participant?.mediaUplinkOwner === 'string'
                && action.participant.mediaUplinkOwner)
                || (typeof action.participant?.[MEDIA_UPLINK_OWNER_PROPERTY] === 'string'
                    && action.participant[MEDIA_UPLINK_OWNER_PROPERTY])
                || remote?.mediaUplinkOwner
                || remote?.[MEDIA_UPLINK_OWNER_PROPERTY]
                || undefined;
            const parsedClaim = parseUplinkClaim(typeof claimRaw === 'string' ? claimRaw : undefined);
            const jwtUserId = stateAfter['features/base/jwt']?.user?.id;
            const localAccountId = getAccountIdentityId(local)
                || (jwtUserId ? String(jwtUserId) : undefined);
            const remoteAccountId = getAccountIdentityId(remote)
                || action.participant?.accountIdentityId
                || action.participant?.[ACCOUNT_IDENTITY_PROPERTY]
                || parsedClaim?.accountId;
            const sameAccount = Boolean(localAccountId
                && remoteAccountId
                && String(remoteAccountId) === String(localAccountId));
            const touchesUplink = Boolean(claimRaw);
            // 仅当「同账号远端」更新了上行声明时 force（force 内再判是否仍是 owner）
            if (isRemoteUpdate && touchesUplink) {
                agentDebugLog('H6', 'multi-device/middleware.ts:PARTICIPANT_*', 'remote uplink update seen', {
                    updatedId,
                    localId: local?.id || null,
                    sameAccount,
                    localAccountId: localAccountId || null,
                    remoteAccountId: remoteAccountId || null,
                    claim: typeof claimRaw === 'string' ? claimRaw : null,
                    parsedEndpoint: parsedClaim?.endpointId || null,
                    parsedTs: parsedClaim?.ts ?? null
                });
                if (sameAccount) {
                    forceLocalReceiveOnly(store);
                    logParticipantInventory(store, `${action.type}:remote-sibling`);
                }
            }
            // 账号身份到位后强制刷新宫格过滤
            if (action.participant?.[ACCOUNT_IDENTITY_PROPERTY]
                || action.participant?.accountIdentityId) {
                try {
                    updateRemoteParticipants(store, true);
                }
                catch (e) {
                    logger.warn('[multi-device] 刷新宫格失败', e);
                }
            }
            return result;
        }
        case PARTICIPANT_LEFT: {
            const leftId = action.participant?.id;
            const result = next(action);
            if (isMultiDeviceModeEnabled(getState())) {
                if (!hasSiblingDevices(getState())) {
                    const local = getLocalParticipant(getState());
                    if (local?.id) {
                        claimMediaUplinkOwner(store);
                    }
                }
            }
            return result;
        }
        case CONFERENCE_JOINED: {
            const result = next(action);
            if (isMultiDeviceModeEnabled(getState())) {
                publishAccountIdentity(store);
                logParticipantInventory(store, 'CONFERENCE_JOINED');
                const local = getLocalParticipant(getState());
                if (local?.id && !hasSiblingDevices(getState())) {
                    const conference = getCurrentConference(getState());
                    const accountId = publishAccountIdentity(store)
                        || getAccountIdentityId(getLocalParticipant(getState()));
                    if (accountId) {
                        const claimValue = encodeUplinkClaim(accountId, local.id, Date.now());
                        conference?.setLocalParticipantProperty(MEDIA_UPLINK_OWNER_PROPERTY, claimValue);
                        dispatch(participantUpdated({
                            id: local.id,
                            local: true,
                            [MEDIA_UPLINK_OWNER_PROPERTY]: claimValue,
                            [ACCOUNT_IDENTITY_PROPERTY]: accountId
                        }));
                    }
                }
            }
            return result;
        }
        case CONFERENCE_LEFT:
            break;
    }
    return next(action);
});
